import React, { useMemo, useState } from 'react';

import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import lockSvg from '@assets/img/icons/lock.svg';
import unlockSvg from '@assets/img/icons/unlock.svg';
import { Button } from '@components/Button';
import { preAlertStrings } from '@data/preAlert';
import { useUser } from '@hooks/queries/useUser';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useFormUtils } from '@hooks/useFormUtils';
import CircularProgress from '@material-ui/core/CircularProgress';
import { DashboardPreAlertListItem, PreAlertHelper } from '@models/next/PreAlert';
import { useQueryClient } from '@tanstack/react-query';
import { foldMessagesEither } from '@utils/errors';
import { mapQueriesData } from '@utils/types';

export const BookingLedgerLock = React.memo(({ item }: { item: DashboardPreAlertListItem }) => {
  const userQuery = useUser();

  const canLock = useMemo(
    () => userQuery.data && PreAlertHelper.canLockLedgerLock(userQuery.data, item),
    [userQuery.data, item],
  );

  const [invalidating, setInvalidating] = useState(false);

  const { renderDialog, confirm } = useConfirmDialog({
    actionableDialogProps: {
      muiDialogProps: {
        disablePortal: false,
      },
    },
  });

  const queryClient = useQueryClient();

  const patchPreAlert = dashboardPreAlertApis.usePatch(
    '/dashboard/pre-alert/:d2d_no',
    {
      params: {
        d2d_no: item.d2d_no,
      },
    },
    {
      async onSuccess() {
        try {
          queryClient.invalidateQueries(
            dashboardPreAlertApis.getKeyByPath('get', '/dashboard/pre-alert/:d2d_no'),
          );
          queryClient.invalidateQueries(
            dashboardPreAlertApis.getKeyByPath('get', '/dashboard/pre-alert/count'),
          );
          await queryClient.invalidateQueries(
            dashboardPreAlertApis.getKeyByPath('get', '/dashboard/pre-alert'),
          );
        } finally {
          setInvalidating(false);
        }
      },
    },
  );

  const isLoading = invalidating || patchPreAlert.isLoading;

  const { renderAlertDialog } = useFormUtils({
    mutations: [patchPreAlert],
    confirmDialogProps: {
      actionableDialogProps: {
        muiDialogProps: {
          disablePortal: false,
        },
      },
    },
  });

  if (!canLock) return null;

  return (
    <>
      {renderDialog()}
      {renderAlertDialog()}

      <Button
        size="small"
        disabled={!userQuery.data}
        color={
          item.is_ledger_locked
            ? 'accent'
            : foldMessagesEither(
                canLock,
                () => 'grey',
                () => 'primary',
              )
        }
        style={{
          width: 45,
        }}
        shape="round"
        text={
          isLoading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <img
              style={{
                width: 21,
                height: 21,
                opacity: item.is_ledger_locked
                  ? undefined
                  : foldMessagesEither(
                      canLock,
                      () => 0.8,
                      () => 1,
                    ),
              }}
              src={item.is_ledger_locked ? unlockSvg : lockSvg}
            />
          )
        }
        onClick={mapQueriesData({ user: userQuery }, ({ user }) => async () => {
          if (isLoading) {
            return;
          }

          if (item.is_ledger_locked) {
            const canUnlock = PreAlertHelper.canUnlockLedgerLock(user);

            await foldMessagesEither(
              canUnlock,
              async (e) => {
                await confirm({
                  message: e.messages,
                });
              },
              async () => {
                if (
                  await confirm({
                    message: preAlertStrings.warningUnlockLedgerLock,
                  }).then((r) => r === 'confirmed')
                ) {
                  await patchPreAlert.mutateAsync({
                    is_ledger_locked: false,
                  });
                }
              },
            );
          } else {
            await foldMessagesEither(
              canLock,
              async (e) => {
                await confirm({
                  message: e.messages,
                });
              },
              async () => {
                if (
                  await confirm({
                    message: preAlertStrings.warningLockLedgerLock,
                  }).then((r) => r == 'confirmed')
                ) {
                  await patchPreAlert.mutateAsync({
                    is_ledger_locked: true,
                  });
                }
              },
            );
          }
        })}
      />
    </>
  );
});
